import { useState, useCallback, useMemo, useEffect } from 'react';
import type { ProductVariant, Product } from '../services/api';

// Minimum order value in dollars
export const MINIMUM_ORDER_VALUE = 300;

// Quantity discount configuration
// If customer orders 3+ of any item, they get $2.50 off each unit
export const QUANTITY_DISCOUNT_THRESHOLD = 3;
export const QUANTITY_DISCOUNT_AMOUNT = 2.50;

// localStorage persistence
const CART_STORAGE_KEY = 'top-dog-cart';
const CART_VERSION = 2; // Bumped to reset carts missing quantityDiscountEnabled field

interface StoredCart {
  version: number;
  items: [string, CartItem][];
}

function loadCart(): Map<string, CartItem> {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return new Map();
    const parsed: StoredCart = JSON.parse(raw);
    if (parsed.version !== CART_VERSION) {
      localStorage.removeItem(CART_STORAGE_KEY);
      return new Map();
    }
    return new Map(parsed.items);
  } catch {
    return new Map();
  }
}

function saveCart(items: Map<string, CartItem>) {
  try {
    const data: StoredCart = {
      version: CART_VERSION,
      items: Array.from(items.entries()),
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silently fail (private browsing, storage full, etc.)
  }
}

export interface CartItem {
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string;
  sku: string;
  quantity: number;
  retailPrice: number;
  wholesalePrice: number;
  imageUrl: string | null;
  quantityDiscountEnabled: boolean;
}

export interface CartSummary {
  itemCount: number;
  totalQuantity: number;
  retailTotal: number;
  wholesaleTotal: number;         // Total before quantity discounts
  quantityDiscount: number;       // Total quantity discount amount
  finalTotal: number;             // Total after quantity discounts
  savings: number;                // Total savings (retail - final)
  meetsMinimum: boolean;
  amountToMinimum: number;
  itemsWithQuantityDiscount: number; // Number of line items qualifying for discount
}

export function useCart() {
  const [items, setItems] = useState<Map<string, CartItem>>(() => loadCart());

  // Persist to localStorage whenever items change
  useEffect(() => {
    saveCart(items);
  }, [items]);

  /**
   * Add item to cart or update quantity
   */
  const addItem = useCallback((
    product: Product,
    variant: ProductVariant,
    quantity: number
  ) => {
    if (quantity <= 0) {
      removeItem(variant.id);
      return;
    }

    setItems(prev => {
      const newItems = new Map(prev);
      newItems.set(variant.id, {
        variantId: variant.id,
        productId: product.id,
        productTitle: product.title,
        variantTitle: variant.title,
        sku: variant.sku,
        quantity,
        retailPrice: parseFloat(variant.price),
        wholesalePrice: parseFloat(variant.wholesalePrice),
        imageUrl: variant.imageUrl || product.imageUrl,
        quantityDiscountEnabled: product.settings?.quantityDiscountEnabled ?? true,
      });
      return newItems;
    });
  }, []);

  /**
   * Update quantity for existing item
   */
  const updateQuantity = useCallback((variantId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(variantId);
      return;
    }

    setItems(prev => {
      const newItems = new Map(prev);
      const item = newItems.get(variantId);
      if (item) {
        newItems.set(variantId, { ...item, quantity });
      }
      return newItems;
    });
  }, []);

  /**
   * Remove item from cart
   */
  const removeItem = useCallback((variantId: string) => {
    setItems(prev => {
      const newItems = new Map(prev);
      newItems.delete(variantId);
      return newItems;
    });
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    setItems(new Map());
  }, []);

  /**
   * Get quantity for a specific variant
   */
  const getQuantity = useCallback((variantId: string): number => {
    return items.get(variantId)?.quantity || 0;
  }, [items]);

  /**
   * Calculate cart summary
   */
  const summary = useMemo((): CartSummary => {
    let totalQuantity = 0;
    let retailTotal = 0;
    let wholesaleTotal = 0;
    let quantityDiscount = 0;
    let itemsWithQuantityDiscount = 0;

    items.forEach(item => {
      totalQuantity += item.quantity;
      retailTotal += item.retailPrice * item.quantity;
      wholesaleTotal += item.wholesalePrice * item.quantity;

      // Apply quantity discount: 3+ units = $2.50 off each unit
      // Only apply if quantityDiscountEnabled is true for this product
      if (item.quantityDiscountEnabled && item.quantity >= QUANTITY_DISCOUNT_THRESHOLD) {
        quantityDiscount += QUANTITY_DISCOUNT_AMOUNT * item.quantity;
        itemsWithQuantityDiscount++;
      }
    });

    const finalTotal = wholesaleTotal - quantityDiscount;
    const savings = retailTotal - finalTotal;
    const meetsMinimum = finalTotal >= MINIMUM_ORDER_VALUE;
    const amountToMinimum = meetsMinimum ? 0 : MINIMUM_ORDER_VALUE - finalTotal;

    return {
      itemCount: items.size,
      totalQuantity,
      retailTotal,
      wholesaleTotal,
      quantityDiscount,
      finalTotal,
      savings,
      meetsMinimum,
      amountToMinimum,
      itemsWithQuantityDiscount,
    };
  }, [items]);

  /**
   * Get all items as array
   */
  const cartItems = useMemo(() => Array.from(items.values()), [items]);

  /**
   * Get items formatted for API
   */
  const getOrderItems = useCallback(() => {
    return cartItems.map(item => ({
      variantId: item.variantId,
      productId: item.productId,
      quantity: item.quantity,
    }));
  }, [cartItems]);

  return {
    items: cartItems,
    summary,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    getQuantity,
    getOrderItems,
    isEmpty: items.size === 0,
  };
}

export type UseCartReturn = ReturnType<typeof useCart>;
